import React, { useEffect, useMemo, useState } from "react";
import { api, errorMessage } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { formatDateTime } from "@/lib/format";
import { STATUS, AUDIT_ACTIONS } from "@/lib/constants";
import { toast } from "sonner";
import { Search, Eye } from "lucide-react";
import { cn } from "@/lib/utils";
import { AdminAccountsCard } from "@/admin/AdminAccountsCard";

export default function AdminUsersPage() {
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [pending, setPending] = useState(null); // {user, next}
  const [detail, setDetail] = useState(null);

  const load = () => api.get("/admin/users", { params: { include_deleted: includeDeleted } }).then((r) => setItems(r.data.items)).catch((e) => toast.error(errorMessage(e)));
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeDeleted]);

  const filtered = useMemo(() => (items || []).filter((u) => !q || u.email.toLowerCase().includes(q.toLowerCase()) || (u.full_name || "").toLowerCase().includes(q.toLowerCase())), [items, q]);

  const toggle = async () => {
    try {
      await api.patch(`/admin/users/${pending.user.id}`, { is_active: pending.next });
      toast.success(pending.next ? "Bruker aktivert" : "Bruker deaktivert");
      setPending(null);
      load();
    } catch (e) { toast.error(errorMessage(e)); }
  };

  const openDetail = async (u) => {
    try { const r = await api.get(`/admin/users/${u.id}`); setDetail(r.data); } catch (e) { toast.error(errorMessage(e)); }
  };

  return (
    <div className="space-y-6">
      <PageHeader title="Brukere" description="Aktiver eller deaktiver kontoer. Du ser antall fakturaer, men aldri innholdet." />
      <AdminAccountsCard />
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
        <div className="relative max-w-sm flex-1"><Search className="h-4 w-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Søk e-post eller navn…" className="pl-9" data-testid="admin-users-search" /></div>
        <label className="flex items-center gap-2 text-sm"><Switch checked={includeDeleted} onCheckedChange={setIncludeDeleted} data-testid="admin-include-deleted" /> Vis slettede</label>
      </div>
      {!items ? <Skeleton className="h-64" /> : (
        <div className="rounded-xl border bg-card overflow-hidden">
          <Table data-testid="admin-users-table">
            <TableHeader><TableRow><TableHead>Bruker</TableHead><TableHead className="hidden md:table-cell">Opprettet</TableHead><TableHead className="hidden md:table-cell">Sist innlogget</TableHead><TableHead className="text-right">Fakturaer</TableHead><TableHead>Status</TableHead><TableHead className="w-24" /></TableRow></TableHeader>
            <TableBody>
              {filtered.length === 0 ? <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-10">Ingen brukere</TableCell></TableRow> : filtered.map((u) => (
                <TableRow key={u.id} className="h-14" data-testid={`admin-user-row-${u.id}`}>
                  <TableCell><p className="font-medium">{u.full_name}</p><p className="text-xs text-muted-foreground">{u.email}</p></TableCell>
                  <TableCell className="hidden md:table-cell text-sm tnum">{formatDateTime(u.created_at)}</TableCell>
                  <TableCell className="hidden md:table-cell text-sm tnum">{formatDateTime(u.last_login_at)}</TableCell>
                  <TableCell className="text-right tnum">{u.invoice_count}</TableCell>
                  <TableCell>
                    {u.deleted_at ? <span className="inline-flex rounded-full border px-2.5 py-1 text-xs font-medium status-avvist">Slettet</span> : (
                      <label className="flex items-center gap-2 text-sm">
                        <Switch checked={!!u.is_active} onCheckedChange={(v) => setPending({ user: u, next: v })} data-testid={`admin-user-active-switch-${u.id}`} aria-label={u.is_active ? "Deaktiver" : "Aktiver"} />
                        <span className={cn("text-xs font-medium", u.is_active ? "text-success" : "text-destructive")}>{u.is_active ? "Aktiv" : "Deaktivert"}</span>
                      </label>
                    )}
                  </TableCell>
                  <TableCell><Button variant="ghost" size="sm" onClick={() => openDetail(u)} data-testid={`admin-user-detail-${u.id}`}><Eye className="h-4 w-4 mr-1" /> Detaljer</Button></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <AlertDialog open={!!pending} onOpenChange={(o) => !o && setPending(null)}>
        <AlertDialogContent className="bg-card">
          <AlertDialogHeader><AlertDialogTitle>{pending?.next ? "Aktivere bruker?" : "Deaktivere bruker?"}</AlertDialogTitle><AlertDialogDescription>{pending?.user?.email}. {pending?.next ? "Brukeren kan logge inn igjen." : "Brukeren kan ikke logge inn, og innkommende e-post avvises. Dataene beholdes."}</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Avbryt</AlertDialogCancel><AlertDialogAction className={pending?.next ? "" : "bg-destructive text-destructive-foreground hover:bg-destructive/90"} onClick={toggle} data-testid="admin-user-toggle-confirm">{pending?.next ? "Aktiver" : "Deaktiver"}</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Sheet open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <SheetContent className="bg-card w-full sm:max-w-md overflow-auto">
          {detail ? (
            <>
              <SheetHeader><SheetTitle>{detail.full_name}</SheetTitle><SheetDescription>{detail.email}</SheetDescription></SheetHeader>
              <div className="mt-6 space-y-5 text-sm" data-testid="admin-user-detail-panel">
                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-lg border p-3"><p className="text-xs text-muted-foreground">Fakturaer</p><p className="font-heading text-xl font-semibold tnum">{detail.invoice_count}</p></div>
                  <div className="rounded-lg border p-3"><p className="text-xs text-muted-foreground">Leverandører</p><p className="font-heading text-xl font-semibold tnum">{detail.supplier_count}</p></div>
                </div>
                <div><p className="font-medium mb-2">Etter status (kun antall)</p>{Object.keys(detail.by_status).length ? Object.entries(detail.by_status).map(([k, v]) => <div key={k} className="flex justify-between py-1 border-b last:border-b-0"><span className="text-muted-foreground">{STATUS[k]?.label || k}</span><span className="tnum">{v}</span></div>) : <p className="text-muted-foreground">Ingen fakturaer</p>}</div>
                <div className="space-y-1"><p><span className="text-muted-foreground">Opprettet:</span> {formatDateTime(detail.created_at)}</p><p><span className="text-muted-foreground">Sist innlogget:</span> {formatDateTime(detail.last_login_at)}</p><p><span className="text-muted-foreground">Innlogging:</span> {detail.auth_provider === "bankid" ? "BankID" : "E-post/passord"}</p><p><span className="text-muted-foreground">Påminnelse:</span> {detail.reminder_days} dager før forfall</p></div>
                <div><p className="font-medium mb-2">Siste aktivitet</p><ul className="divide-y">{detail.recent_activity.map((a) => <li key={a.id} className="py-1.5 flex justify-between gap-2"><span>{AUDIT_ACTIONS[a.action] || a.action}</span><span className="text-xs text-muted-foreground tnum shrink-0">{formatDateTime(a.created_at)}</span></li>)}</ul></div>
                <p className="text-xs text-muted-foreground rounded-lg border bg-secondary p-3">Av personvernhensyn vises ikke leverandører, beløp, KID eller dokumenter for administrator.</p>
              </div>
            </>
          ) : null}
        </SheetContent>
      </Sheet>
    </div>
  );
}
